/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import styled from 'styled-components'
import { toast } from 'react-hot-toast'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Anchor, Badge, Box, Theme } from '@nexigendigital/nxui'
import { serviceAPI } from 'api/service'
import { ServiceStatus, Weight } from 'models/enums'
import { getServiceStatusColour, parseISO, parseSQL } from 'helpers/utils'
import globalCookies from 'helpers/cookies'

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function Overview() {
    const id = parseInt(location.pathname?.split('/')[2], 10)
    const { data } = serviceAPI.endpoints.service.useQueryState(id)
    const { data: meta } = serviceAPI.endpoints.moduleMeta.useQueryState(id)
    const loginURL = `${process.env.REACT_APP_API}client/services/${id}/login`
    const token = globalCookies.get('XSRF-TOKEN')

    async function fetchURLAndOpenLink() {
        const response = await fetch(loginURL, { method: 'GET', headers: { 'X-XSRF-TOKEN': token }, credentials: 'include' })
        const { data, messages } = await response.json()

        if (data.length <= 1 && messages.length >= 1) {
            toast.custom(messages[0], {
                duration: 8000
            })
        } else {
            const { url } = data
            window.open(url)
        }
    }

    return (
        <OverviewWrapper
            header={{
                element: (
                    <Header>
                        <ServiceWrapper>
                            <Name>{data?.name}</Name>
                            <Product>{data?.product.name}</Product>
                        </ServiceWrapper>
                        {data
                            ? <Badge color={getServiceStatusColour(data?.status)}>{data?.status.toUpperCase()}</Badge>
                            : null
                        }
                    </Header>
                )
            }}
            footer={data?.status === ServiceStatus.Active ? <Anchor color={'primary'} onClick={fetchURLAndOpenLink}>{meta?.login_label}</Anchor> : null}
        >
            <Info>
                <Column>
                    <span>Primary Domain Name</span>
                    <span>{data?.name}</span>
                </Column>
                <Column>
                    <span>Hosting Plan</span>
                    <span>{data?.product.name}</span>
                </Column>
                <Column>
                    <span>Activated At</span>
                    <span>{data?.created_at ? parseISO(data?.created_at).setLocale('en-AU').toLocaleString() : null}</span>
                </Column>
                <Column>
                    <span>Next Due Date</span>
                    <span>{data?.next_due_date ? parseSQL(data?.next_due_date).setLocale('en-AU').toLocaleString() : null}</span>
                </Column>
                <Column>
                    <span>Billing Cycle</span>
                    <span>{data?.billing_cycle?.name}</span>
                </Column>
            </Info>
        </OverviewWrapper>
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const OverviewWrapper = styled(Box)`
    ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-xl']))} {
        margin-top: 16px;
    }

    header {
        width: 100%;

        > div {
            width: 100%;
        }
    }

    footer {
        padding: 12px 24px 12px 0;
        display: flex;
        justify-content: flex-end;
    }
`

const Header = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-sm']))} {
        flex-direction: column;
        align-items: flex-start;
        gap: 7px;
    }
`

const ServiceWrapper = styled.div`
    display: flex;
    gap: 5px;
    ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-sm']))} {
        flex-direction: column;
    }
`

const Name = styled.span`
    font-size: 18px;
    ${Theme.Functions.setFont('Inter', Weight.Medium)};
`

const Product = styled.span`
    font-size: 18px;
    color: ${props => props.theme['text--100']};
    ${Theme.Functions.setFont('Inter', Weight.Regular)};
`

const Info = styled.div`
    width: 100%;
    display: flex;
    justify-content: space-around;
    border-top: 1px solid ${props => props.theme['gray--100']};
    border-bottom: 1px solid ${props => props.theme['gray--100']};
    padding: 20px 0;
    ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-sm']))} {
        flex-direction: column;
        padding: 20px;
        gap: 20px;
    }
`

const Column = styled.div`
    font-size: .875rem;
    display: flex;
    flex-direction: column;
    gap: 10px;

    > span {
        &:first-child {
            color: ${props => props.theme['text--100']};
        }

        &:last-child {
            ${Theme.Functions.setFont('Inter', Weight.Medium)};
        }
    }
`
